import { Component, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DropDownModel } from 'app/modules/common/entities/DropDownModel';
import { Criteria, Rule, RuleType } from 'app/modules/common/entities/leave-import/criteria.model';
import { CommonDialogService } from 'app/modules/common/services/common-dialog.service';
import { PageLoaderService } from 'app/modules/common/services/page-loader.service';
import { SharedService } from 'app/modules/common/services/shared-service';
import { finalize } from 'rxjs/operators';
import { LeaveImportRulesService } from '../../services/leave-import-rules.service';
import { NewRuleDialogComponent, NewRuleDialogData } from '../new-rule-dialog/new-rule-dialog.component';

@Component({
    selector: 'app-criterios',
    templateUrl: './criterios.component.html',
    styleUrls: ['./criterios.component.scss'],
    standalone: false
})
export class CriteriosComponent implements OnInit {

    public criteriosList: DropDownModel[] = [];
    public ruleTypes: RuleType[] = [];
    public rulesForms: UntypedFormArray = new UntypedFormArray([]);
    public selectedCriteria = -1;
    public rulesAreLoading = false;


    constructor(
        private rulesService: LeaveImportRulesService,
        private sharedService: SharedService,
        private fb: UntypedFormBuilder,
        private dialogService: CommonDialogService,
        private dialog: MatDialog,
        private pageLoader: PageLoaderService,
        private dialogRef: MatDialogRef<CriteriosComponent>
    ) { }

    ngOnInit(): void {
        this.loadRuleTypes();
        this.listenCriteriaChanges();
    }


    /**
     * Handle cancel button click
     */
    public onClose() {
        this.dialogRef.close();
    }


    //#region CRITERIA

    /**
     * Listen for changes in criterias
     */
    private listenCriteriaChanges() {
        this.rulesService.criterias.subscribe(res => {

            this.criteriosList = res;

            // select first criteria if the selected one does not exist
            const selectedCriteriaItem = this.criteriosList.filter(c => parseInt(c.value) === this.selectedCriteria);

            if (selectedCriteriaItem.length === 0 && this.criteriosList.length > 0)
                this.selectCriteria(parseInt(this.criteriosList[0].value));
            else if (selectedCriteriaItem.length === 0)
                this.selectedCriteria = -1;

        }, err => {
            this.sharedService.ShowSnackBar("Error al cargar los criteras de las tareas");
            console.error(err);
        });

    }


    /**
     * Create criteria button click even handler
     */
    public onCreateCriteria() {
        // ask user a name for new criteria
        this.dialogService.openPromptDialog(
            "Por favor, indique un nombre",
            async (input) => await this.createCriteria(input) ,
        );
    }


    /**
     * Create a criteria
     * @param criteriaName New criteria name
     * @returns True if the criteria is crated. Otherwise false
     */
    public async createCriteria(criteriaName: string): Promise<boolean> {
        this.pageLoader.ShowPageLoader();

        const criteria = new Criteria(criteriaName);

        try {

            const res = await this.rulesService
                .createCriteria(criteria)
                .toPromise();

            if (res.code === 0) {

                this.pageLoader.HidePageLoader();

                // select new criteria
                this.selectCriteria(res.result.criterioId);
                return true;

            } else {
                this.sharedService.ShowSnackBar("Lo siento, la creación de criterios ha fallado");
                console.error(res.message);
            }

        } catch (error) {
            this.sharedService.ShowSnackBar("Lo siento, la creación de criterios ha fallado");
            console.error(error);

        }

        this.pageLoader.HidePageLoader();
        return false;

    }


    /**
     * Criteria update click event listener
     * @param criteria Criteria as a drop down value
     */
    public onUpdateCriteria(criteria: DropDownModel) {
        // ask user a new name
        this.dialogService.openPromptDialog(
            "Por favor, proporcione un nuevo nombre para los criterios",
            async (input) => await this.updateCriteria(parseInt(criteria.value), input),
            true,
            criteria.viewValue,
        );
    }


    /**
     * Update a criteria
     * @param id ID of the criteria to be updated
     * @param name New name of the criteria
     * @returns True if criteria is updated. False if not
     */
    private async updateCriteria(id: number, name: string): Promise<boolean> {
        this.pageLoader.ShowPageLoader();

        const criteria = new Criteria(name);

        try {

            const res = await this.rulesService.updateCriteria(id, criteria).toPromise();

            if (res.code === 0) {

                this.pageLoader.HidePageLoader();
                return true;

            } else {
                this.sharedService.ShowSnackBar("Lo sentimos, la actualización de los criterios ha fallado");
                console.error(res.message);
            }

        } catch (error) {
            this.sharedService.ShowSnackBar("Lo sentimos, la actualización de los criterios ha fallado");
            console.error(error);
        }

        this.pageLoader.HidePageLoader();
        return false;
    }


    /**
     * Delete criteria event handler
     */
    public onDeleteCriteria(id: number) {
        // get user confirmation to delete
        const diagRef = this.dialogService.openConfirmationDialog("¿Eliminar el criterio?");
        diagRef
            .afterClosed()
            .subscribe((res: boolean) => {
                if (res)
                    this.deleteCriteria(id);
            });
    }


    /**
     * Delete a criteria
     */
    private deleteCriteria(id: number) {
        this.rulesService
            .deleteCriteria(id)
            .subscribe(res => {

                if (res.code === 0) {
                    this.sharedService.ShowSnackBar("Los criterios se han eliminado con éxito");
                } else {
                    this.sharedService.ShowSnackBar("Lo siento, la eliminación de criterios ha fallado");
                    console.error("Server error: " + res.message);
                }

            }, err => {
                this.sharedService.ShowSnackBar("Lo siento, la eliminación de criterios ha fallado");
                console.error(err);
            });
    }

    //#endregion


    //#region RULES

    /**
     * Select the given criteria and loads its rules
     * @param criterioId Criterios ID
     */
    public selectCriteria(criterioId: number) {
        this.rulesAreLoading = true;

        this.rulesService
            .getRules(criterioId)
            .pipe(
                finalize(() => this.rulesAreLoading = false)
            )
            .subscribe(res => {

                if (res.code === 0) {

                    // create form array using rules
                    this.rulesForms = this.convertRulesIntoForm(res.result);

                    // set selected criteria ID
                    this.selectedCriteria = criterioId;

                } else {
                    this.sharedService.ShowSnackBar("Error en la carga de las reglas");
                    console.error("Server error: " + res.message);
                }

            }, err => {
                this.sharedService.ShowSnackBar("Error en la carga de las reglas");
                console.error(err);
            });
    }


    /**
     * Reload rules of currently selected criteria
     */
    private reloadRules() {
        // reselect the same criteria to reload its rules
        this.selectCriteria(this.selectedCriteria);
    }


    /**
     * Load rule types into dropdown
     */
    private loadRuleTypes() {
        this.rulesService
            .getRuleTypes()
            .subscribe(res => {

                if (res.code === 0) {
                    this.ruleTypes = res.result;
                } else {
                    this.sharedService.ShowSnackBar("Error en la carga de los tipos de reglas");
                    console.error("Server error: " + res.message);
                }

            }, err => {
                this.sharedService.ShowSnackBar("Error en la carga de los tipos de reglas");
                console.error(err);
            });
    }


    /**
     * Convert the given rules into `FormArray`
     * @param rules Criteria rules
     * @returns `FormArray` with array of form groups for each rule
     */
    private convertRulesIntoForm(rules: Rule[]): UntypedFormArray {

        // create form groups from rules
        const formGroups = rules.map(r => this.convertRuleIntoForm(r));

        return this.fb.array(formGroups);

    }


    /**
     * Covert given rule into a form
     * @param rule Rule
     * @returns Form
     */
    private convertRuleIntoForm(rule: Rule) {
        // get selected rule type to get parameters
        const selectedRuleType = this.ruleTypes.filter(rType => rType.tipoReglaId === rule.tipoReglaId)[0];

        const form = this.fb.group({
            criterioId: rule.criterioId,
            id: rule.id,
            prioridad: rule.prioridad,
            tipoReglaId: [rule.tipoReglaId, Validators.required],
            parametro1: selectedRuleType?.parametro,
            param1: [rule.param1, Validators.required],
            parametro2: selectedRuleType?.parametro2,
            param2: [rule.param2, Validators.required],
            tareaId: [rule.tareaId, Validators.required],
            dias: [rule.dias, Validators.max(30)],
        });

        // disable parameters and their values if they are null
        if (!selectedRuleType?.parametro) {
            form.controls.parametro1.disable();
            form.controls.param1.disable();
        }
        if (!selectedRuleType?.parametro2) {
            form.controls.parametro2.disable();
            form.controls.param2.disable();
        }

        // add rule type change listner
        form.controls.tipoReglaId.valueChanges.subscribe((_v) => {
            this.onRuleTypeChanges(form);
        });

        return form;
    }


    /**
     * Change the parameter controls when rule type changes
     * @param form Rule form where rule type was changed. This forms values will be mutated
     */
    private onRuleTypeChanges(form: UntypedFormGroup) {
        // get rule type ID
        const tipoReglaId: number = form.controls.tipoReglaId.value;
        const selectedRuleType = this.ruleTypes.filter(rType => rType.tipoReglaId === tipoReglaId)[0];

        // disable parameters and their values if they are null
        // otherwise enable and set their values
        if (!selectedRuleType?.parametro) {
            form.controls.parametro1.disable();
            form.controls.param1.disable();
            form.controls.parametro1.setValue(null);
        } else {
            form.controls.parametro1.enable();
            form.controls.param1.enable();
            form.controls.parametro1.setValue(selectedRuleType.parametro);
        }

        if (!selectedRuleType?.parametro2) {
            form.controls.parametro2.disable();
            form.controls.param2.disable();
            form.controls.parametro2.setValue(null);
        } else {
            form.controls.parametro2.enable();
            form.controls.param2.enable();
            form.controls.parametro2.setValue(selectedRuleType.parametro2);
        }
    }


    /**
     * Rule delete event handler
     * @param form Form of rule to be deleted
     */
    public onDeleteRule(form: UntypedFormGroup) {
        // get confitmation before deleting
        const diagRef = this.dialogService.openConfirmationDialog("¿Eliminar regla?");
        diagRef
            .afterClosed()
            .subscribe((res: boolean) => {
                if (res)
                    this.deleteRule(form.controls.id.value);
            });
    }


    /**
     * Delete a rule 
     */
    private deleteRule(ruleId: number) {
        this.pageLoader.ShowPageLoader();

        this.rulesService
            .deleteRule(ruleId)
            .pipe(
                finalize(() => this.pageLoader.HidePageLoader())
            )
            .subscribe(res => {

                if (res.code === 0) {
                    this.sharedService.ShowSnackBar("La regla se ha eliminado con éxito");

                    // reload rules
                    this.reloadRules();
                } else {
                    console.error("Server error: " + res.message);
                    this.sharedService.ShowSnackBar("Lo siento, el borrado de la regla ha fallado");
                }

            }, err => {
                console.error(err);
                this.sharedService.ShowSnackBar("Lo siento, el borrado de la regla ha fallado");
            });
    }


    /**
     * Rule update event handler
     * @param form Rule form
     */
    public onUpdateRule(form: UntypedFormGroup) {
        // validate form
        form.markAllAsTouched();
        if (!form.valid)
            return;

        const rule: Rule = form.value;
        this.updateRule(rule.id, rule);
    }


    /**
     * Update a rule
     * @param id Rule ID
     * @param rule Rule data
     */
    private updateRule(id: number, rule: Rule) {
        this.pageLoader.ShowPageLoader();

        this.rulesService
            .updateRule(id, rule)
            .pipe(
                finalize(() => this.pageLoader.HidePageLoader())
            )
            .subscribe(res => {

                if (res.code === 0) {
                    this.sharedService.ShowSnackBar("La regla se ha actualizado con éxito");
                    this.reloadRules();
                } else {
                    console.error("Server error: " + res.message);
                    this.sharedService.ShowSnackBar("Lo siento, la actualización de la regla ha fallado");
                }

            }, err => {
                console.error(err);
                this.sharedService.ShowSnackBar("Lo siento, la actualización de la regla ha fallado");
            });
    }


    /**
     * Handle create rule event
     */
    public onCreateRule() {

        const dialogData: NewRuleDialogData = {
            criteriaId: this.selectedCriteria,
        };
        const diagRef = this.dialog.open(
            NewRuleDialogComponent,
            {
                data: dialogData,
            }
        );

        // if a rule is created dialog will return true
        diagRef.afterClosed().subscribe((res: boolean) => {
            if (res) this.reloadRules();
        });

    }

    //#endregion

}
